/*
 * VEBLENIST Shipping Protection (replaces Captain Shipping Protection)
 * ---------------------------------------------------------------------------
 * Cart page only (theme cart type = "page"). Loaded from layout/theme.liquid.
 *
 * Design rules that keep it from fighting VO Product Options or the theme:
 *  - Never reloads the page. Cart changes go through /cart/update.js, then the
 *    theme's own "cart:refresh" event re-renders the cart section in place.
 *  - Only reacts to (a) the customer clicking the toggle and (b) a mismatch
 *    between the rendered cart and the desired state after a render. It does
 *    not wrap fetch/XHR or listen to other apps' cart events.
 *  - Loop guard: the same correction is never sent twice, and at most
 *    MAX_FIXES automatic corrections happen per page view.
 *  - Checkout is disabled while a change is in flight, so the customer can't
 *    reach checkout with a half-updated cart.
 */
(function () {
  'use strict';

  var DEFAULT_ON = true;          // protection is pre-selected for new carts
  var DECLINE_KEY = '_sp_declined'; // cart attribute; "_" hides it at checkout
  var MAX_FIXES = 4;              // automatic corrections allowed per page view
  var RERENDER_TIMEOUT = 3500;

  var busy = false;
  var fixes = 0;
  var lastKey = null;
  var retriedRefresh = false;
  var lastWaitTimedOut = false;
  var failedState = false;       // a cart update failed on this page view

  function node() { return document.querySelector('[data-vb-sp]'); }

  function parseLines(str) {
    var map = {};
    (str || '').split(',').forEach(function (pair) {
      if (!pair) return;
      var parts = pair.split(':');
      map[parts[0]] = (map[parts[0]] || 0) + parseInt(parts[1], 10);
    });
    return map;
  }

  function readState(el) {
    var d = el.dataset;
    return {
      target: d.spTarget || '',
      lines: parseLines(d.spLines),
      real: parseInt(d.spRealCount || '0', 10),
      declined: d.spDeclined === '1'
    };
  }

  // What the protection lines SHOULD be: {targetId: 1} or {}.
  function desiredLines(s, wantOn) {
    var m = {};
    if (wantOn && s.real > 0 && s.target) m[s.target] = 1;
    return m;
  }

  // /cart/update.js "updates" payload turning `actual` into `want`, or null.
  function diff(actual, want) {
    var u = {};
    var changed = false;
    Object.keys(actual).forEach(function (id) {
      var q = want[id] || 0;
      if (q !== actual[id]) { u[id] = q; changed = true; }
    });
    Object.keys(want).forEach(function (id) {
      if (!(id in actual)) { u[id] = want[id]; changed = true; }
    });
    return changed ? u : null;
  }

  function wantOnFor(s) {
    if (s.real === 0 || s.declined) return false;
    return DEFAULT_ON || Object.keys(s.lines).length > 0;
  }

  function setBusy(on) {
    busy = on;
    var el = node();
    if (el) {
      el.classList.toggle('is-busy', on);
      el.setAttribute('aria-busy', on ? 'true' : 'false');
    }
    var buttons = document.querySelectorAll('button[name="checkout"], .Cart__Checkout');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = on;
      buttons[i].classList.toggle('vb-sp-wait', on);
    }
  }

  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  // Resolves once the theme has swapped in new cart markup (or after a timeout).
  function waitForRerender(oldEl) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() {
        var current = node();
        var replaced = oldEl ? (current !== oldEl) : !!current;
        if (replaced) { lastWaitTimedOut = false; return resolve(); }
        if (Date.now() - start > RERENDER_TIMEOUT) { lastWaitTimedOut = true; return resolve(); }
        setTimeout(poll, 50);
      })();
    });
  }

  function apply(updates, attributes) {
    var oldEl = node();
    var failed = false;
    var body = { updates: updates || {} };
    if (attributes) body.attributes = attributes;
    setBusy(true);

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart update failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        refreshCart();
        return waitForRerender(oldEl);
      })
      .catch(function (err) {
        if (window.console) console.warn('[vb-shipping-protection]', err);
        failed = true;
      })
      .then(function () {
        setBusy(false);
        if (failed) { markFailed(); return; }
        verify(updates);
      });
  }

  // The cart could not be updated (network error, product unavailable for this
  // customer, ...). Never leave the switch showing ON with no fee in the cart.
  function markFailed() {
    var el = node();
    if (!el) return;
    var s = readState(el);
    var input = el.querySelector('[data-vb-sp-input]');
    if (input) input.checked = Object.keys(s.lines).length > 0;
    el.setAttribute('data-sp-error', '1');
    failedState = true; // stays OFF across theme redraws until the customer tries again
  }

  // After a change: if the markup still shows the pre-change cart (a stale
  // render won the race), ask the theme for one more render. Never loops.
  function verify(sentUpdates) {
    var el = node();
    if (!el) return;
    var s = readState(el);
    var pending = diff(s.lines, desiredLines(s, wantOnFor(s)));
    if (pending && sentUpdates && JSON.stringify(pending) === JSON.stringify(sentUpdates) && !retriedRefresh && !lastWaitTimedOut) {
      retriedRefresh = true;
      var oldEl = el;
      setBusy(true);
      refreshCart();
      waitForRerender(oldEl).then(function () { setBusy(false); sync(); });
      return;
    }
    sync();
  }

  // Bring the cart in line with the desired state (default-on, tier changes,
  // stray protection line in an otherwise empty cart).
  function sync() {
    var el = node();
    if (!el || busy) return;
    if (failedState) { markFailed(); return; }
    var s = readState(el);
    var updates = diff(s.lines, desiredLines(s, wantOnFor(s)));
    if (!updates) {
      // Cart matches the desired state: a genuine loop can never reach here,
      // so it is safe to allow future (legitimate) corrections again.
      lastKey = null;
      fixes = 0;
      return;
    }

    var key = JSON.stringify(updates);
    if (key === lastKey || fixes >= MAX_FIXES) return;
    lastKey = key;
    fixes++;
    apply(updates);
  }

  // Customer flips the toggle.
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-vb-sp-input')) return;
    var el = node();
    if (!el) return;
    if (busy) { input.checked = !input.checked; return; }

    var s = readState(el);
    var attrs = {};
    attrs[DECLINE_KEY] = input.checked ? '' : '1';
    var updates = diff(s.lines, desiredLines(s, input.checked)) || {};

    lastKey = null;
    fixes = 0;
    retriedRefresh = false;
    failedState = false;
    el.removeAttribute('data-sp-error');
    apply(updates, attrs);
  });

  // The theme swaps the whole cart markup in place after any cart change
  // (quantity, remove, cart:refresh). Re-check the desired state whenever the
  // toggle node is replaced. Loop guards above still cap automatic fixes.
  function watchRerenders() {
    var section = document.body;
    if (!section || !window.MutationObserver) return;
    var known = node();
    var pending = null;
    new MutationObserver(function () {
      var current = node();
      if (current === known) return;
      known = current;
      if (busy) { setBusy(true); return; }
      clearTimeout(pending);
      pending = setTimeout(sync, 150);
    }).observe(section, { childList: true, subtree: true });
  }

  function init() { watchRerenders(); sync(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Back/forward cache: the theme re-renders the cart on pageshow; re-check after.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    lastKey = null;
    fixes = 0;
    retriedRefresh = false;
    setTimeout(sync, 400);
  });
})();
